import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    logger,
    VerificationStepsEnum,
} from '@sheerid/jslib';


const DOC_UPLOAD_POLL_DURATION = 72000000;
const SSO_POLL_DURATION = 5000;

const defaultDocUploadSubtitle = `SheerID is reviewing your documents, to confirm your
                        status. This process may take up to 20 minutes.`;

const defaultDocUploadSubtitle3 = 'You will be contacted via email.';

const defaultSSOSubtitle = "Please sign in to your school's web portal. It will open in a new tab.";

const defaultSSOSubtitle2 = "Can't sign in to your school?";

interface StepPendingProps {
    verificationService: VerificationService;
}

const StepPending = ({ verificationService }: StepPendingProps) => {
    const { programId } = verificationService;
    const { viewModel } = verificationService;
    const previousVerificationResponse = verificationService.previousVerificationResponse || undefined;
    const { verificationId } = verificationService.verificationResponse;

    const isPreviousStepSSO = (): boolean => {
        if (previousVerificationResponse && previousVerificationResponse.currentStep === VerificationStepsEnum.sso) {
            return true;
        }
        return false;
    };

    const cancelSSOStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepSSOComponent submitting cancel request');
        clearTimeout(timeout);
        verificationService.submitStep(VerificationStepsEnum.sso, nextState, verificationService.previousVerificationResponse);
    };

    const pollForVerificationServiceUpdates = () => setTimeout(
        () => {
            verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse);
        },
        isPreviousStepSSO() ? SSO_POLL_DURATION : DOC_UPLOAD_POLL_DURATION,
    );

    const askForVerificationServiceUpdates = () => setTimeout(
        () => {
            verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse);
        },
    );

    const timeout = pollForVerificationServiceUpdates();

    return (
        <div id="sid-step-pending" className="sid-l-container">
            <img className="sid-header__logo" src="https://assets-resources.sheerid.com/jcrew/img/j.crew_logo_2022.png" alt="" />
            {
                isPreviousStepSSO()
                    ? (
                        <div className="sid-header sid-l-horz-center">
                            <div className="sid-l-horz-center" />
                            <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                                <FormattedHTMLMessage id="step.sso.title" defaultMessage="Verify using your school credentials" />
                            </div>
                            <div className="sid-header__subtitle-one">
                                <FormattedHTMLMessage
                                    id="step.sso.subtitle"
                                    defaultMessage={defaultSSOSubtitle}
                                />
                            </div>
                            <div className="sid-l-space-top-xl" />
                            <div className="sid-header__subtitle-two sid-h-small-text sid-l-space-top-md">
                                <FormattedHTMLMessage
                                    id="step.sso.subtitle2"
                                    defaultMessage={defaultSSOSubtitle2}
                                />
                                <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                                    <button
                                        onClick={cancelSSOStep}
                                        type="submit"
                                        className="sid-btn sid-btn--dark sid-l-full-width"
                                        aria-label="submit"
                                    >
                                        <FormattedHTMLMessage id="step.sso.cancel" defaultMessage="Upload Proof of Enrollment" />
                                    </button>
                                </div>
                            </div>
                            <div className="sid-l-space-top-xl" />
                        </div>
                    )
                    : (
                        <div className="sid-header sid-l-horz-center">
                            <div className="sid-l-horz-center" />

                            <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                                <FormattedHTMLMessage id="step.pending.title" defaultMessage="Under Review" />
                            </div>

                            <div className="sid-pending-blocks">
                                <FormattedHTMLMessage
                                    id="step.pending.subtitle"
                                    defaultMessage={defaultDocUploadSubtitle}
                                />
                            </div>

                            <div className="sid-pending-blocks">
                                <FormattedHTMLMessage id="step.success.returnTo" defaultMessage="Please return to <a href='https://jcrew.com' target='_blank' class='sid-jcrew-link'>jcrew.com</a> to continue shopping." />
                            </div>

                            <div className="sid-pending-blocks">
                                <button
                                    onClick={askForVerificationServiceUpdates}
                                    onKeyPress={askForVerificationServiceUpdates}
                                    type="submit"
                                    className="sid-btn sid-btn--dark sid-l-full-width"
                                    aria-label="update this page"
                                >
                                    <FormattedHTMLMessage id="step.success.checkForUpdate" defaultMessage="Click here to update this page" />
                                </button>
                            </div>

                            <div className="sid-pending-blocks">
                                <FormattedHTMLMessage
                                    id="step.pending.subtitle3"
                                    defaultMessage={defaultDocUploadSubtitle3}
                                />
                            </div>

                        </div>
                    )
            }

            <div className="sid-last-footer">
                <span className="sid-footer-one">
                    <FormattedHTMLMessage
                        id="lastFooter"
                    />
                </span>

                <p className="sid-footer-two">
                    <FormattedHTMLMessage
                        id="footerOne"
                    />
                    <FormattedHTMLMessage
                        id="footerTwo"
                    />
                </p>
            </div>
        </div>
    );
};

export const StepPendingComponent = injectIntl(StepPending);
